<template>
  <div :class="`bitts-avatar-item-${size}`">
    <div>
      <BittsAvatarPip
        v-if="containsPip"
        :image-source="imageSource"
        :org="org"
        :user="user"
        :include-hover-text="includeHoverText"
        :is-user-main-avatar="isUserMainAvatar"
        :show-initials="showInitials"
        :size="size"
      />
      <BittsAvatar
        v-else
        :shape="shape"
        :image-source="imageSource"
        :org="org"
        :user="user"
        :include-hover-text="includeHoverText"
        :is-user-main-avatar="isUserMainAvatar"
        :show-initials="showInitials"
        :size="size"
      />
    </div>
    <div class="bitts-avatar-item__text">
      <div :class="titleTextClasses" class="bitts-avatar-item__text-title">
        {{ title }}
      </div>
      <div
        v-if="description"
        :class="secondaryTextClasses"
        class="bitts-avatar-item__text-secondary"
      >
        {{ description }}
      </div>
      <slot name="extraLine" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import BittsAvatar from '../BittsAvatar/BittsAvatar.vue';
import type { BittsAvatarProps } from '../BittsAvatar/BittsAvatar.vue';
import BittsAvatarPip from '../BittsAvatarPip/BittsAvatarPip.vue';

export type BittsAvatarItemSpacing = 'default' | 'condensed';
export type BittsAvatarItemSize = 'small' | 'medium';

type SharedAvatarProps = Pick<
  BittsAvatarProps,
  'shape' | 'org' | 'user' | 'imageSource' | 'includeHoverText' | 'showInitials'
>;

const {
  spacing = 'default',
  shape = 'circle',
  size = 'medium',
  hasDarkBg = false,
  org = null,
  user = null,
  title,
  description = '',
  containsPip = false,
  imageSource = '',
  includeHoverText = false,
  isUserMainAvatar = true,
  showInitials = false,
} = defineProps<
  SharedAvatarProps & {
    spacing?: BittsAvatarItemSpacing;
    size?: BittsAvatarItemSize;
    hasDarkBg?: boolean;
    title: string;
    description?: string;
    containsPip?: boolean;
    isUserMainAvatar?: boolean;
  }
>();

const isDefault = computed(() => spacing === 'default');
const isSmall = computed(() => size === 'small');

const titleTextClasses = computed(() => {
  if (isSmall.value) {
    return {
      'text-neutral-text-strong': isDefault.value,
      'text-neutral-text': !isDefault.value,
      'text-white': hasDarkBg,
    };
  }
  return {
    'text-base': true,
  };
});

const secondaryTextClasses = computed(() => {
  if (isSmall.value) {
    return {
      'text-neutral-text': isDefault.value,
      'text-neutral-text-weak': !isDefault.value,
      'text-white': hasDarkBg,
    };
  }
  return {
    'text-base': isDefault.value,
    'text-sm': !isDefault.value,
  };
});
</script>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-avatar-item-medium {
  @apply flex items-center;

  .bitts-avatar-item__text {
    @apply flex flex-col justify-center ml-12;
  }

  .bitts-avatar-item__text-title {
    @apply font-bold text-neutral-900;
  }

  .bitts-avatar-item__text-secondary {
    @apply text-neutral-600;
  }
}

.bitts-avatar-item-small {
  @apply flex items-center;

  .bitts-avatar-item__text {
    @apply flex flex-col justify-center ml-8;
  }

  .bitts-avatar-item__text-title {
    @apply font-bold text-sm text-neutral-text-strong;
  }

  .bitts-avatar-item__text-secondary {
    @apply text-xs text-neutral-600;
  }
}
</style>
